/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext, useState, useEffect } from 'react';
import { Link, Button, Grid, TextField, Typography, Box, FormControl, Divider, FormControlLabel, Checkbox } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import useLocalStorage from '../../../hooks/useLocalStorage';
import useAuthenticationService from '../../../hooks/api/useAuthenticationService';
import microsofticon from '../../../assets/images/microsoft.jpg';
import InitialStoreSelector from './InitialStoreSelector';
import { UserContext } from '../../../components/shared/useUser';
import { User } from '../../../app/services/api/generated';
import { getApiConfig } from '../.,/../../../app/services/environment';
import { jwtDecode } from 'jwt-decode';
import { useLocation } from 'react-router-dom';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import { isEmailValid } from '../../../utils';

const LoginForm = (props: { switchDisplay: () => void }) => {
  const { t } = useTranslation('authentication');
  const theme = useTheme();
  const { login, handleAuthResponse } = useAuthenticationService();
  const { setUserContext } = useContext(UserContext);
  const [storedValue, setValue] = useLocalStorage('token', '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [showSelectStore, setShowSelectStore] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const location = useLocation();
  const apiConfig = getApiConfig();

  const AUTH0_LOGIN_URL = `${apiConfig.auth0AuthoriseBaseUrl}?client_id=${apiConfig.auth0ClientId}` +
    `&audience=${apiConfig.auth0Audience}&response_type=token` +
    `&connection=${apiConfig.auth0dConnectionName}&redirect_uri=${window.location.origin}/login`;

  const extractParamsFromHashOrSearch = (searchOrHash: string) => {
    const params = new URLSearchParams(searchOrHash.replace(/^#/, ''));
    return {
      error: params.get('error'),
      error_description: decodeURIComponent(params.get('error_description') ?? ''),
      access_token: params.get('access_token'),
    };
  };

  useEffect(() => {
    const authResponse = handleAuthResponse();
    if (authResponse?.access_token) {
      const token = authResponse.access_token;
      const userEmail = decodeToken(token);
      if (userEmail) {
        setValue(token);
        setUserContext({ emailAddress: email } as User, token);
        setOpenSnackbar(false);
        setShowSelectStore(true);
      }
      else if (authResponse?.error_message) {
        setSnackbarMessage(authResponse.error_message);
        setOpenSnackbar(true);
      }
      else {
        setSnackbarMessage('');
        setOpenSnackbar(true);
      }
    }
  }, []);

  useEffect(() => {
    const { error, error_description } = extractParamsFromHashOrSearch(location.hash);
    if (error) {
      setSnackbarMessage(error_description ?? '');
      setOpenSnackbar(true);
    }
  }, [location.hash]);

  const decodeToken = (token: string): string | null => {
    try {
      const decoded: any = jwtDecode(token);
      const email = decoded[apiConfig.auth0Audience + '/UserName'];
      if (email) {
        return email;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  };

  const handleEmailChange = (e: any) => {
    setEmail(e.target.value);
    setEmailError('');
  };

  const handlePasswordChange = (e: any) => {
    setPassword(e.target.value);
    setPasswordError('');
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
    setTimeout(() => {
      setSnackbarMessage('');
    }, 300);
  };

  const isFormValid = () => {
    let isValid = true;
    if (!isEmailValid(email)) {
      isValid = false;
      setEmailError('Enter an email address in the correct format, like name@example.com');
    }
    if (!password) {
      isValid = false;
      setPasswordError('Enter your password');
    }

    return isValid;
  };

  const loginUser = () => {
    login(email, password)
      .then((result) => {
        if (result.access_token) {
          const token = result.access_token;
          setValue(token);
          setUserContext({ emailAddress: email } as User, token);
          setOpenSnackbar(false);
          setShowSelectStore(true);
        } else {
          setOpenSnackbar(true);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleKeyPress = (event: any) => {
    if (event.key === 'Enter') {
      handleSubmit(event);
    }
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (isFormValid()) {
      loginUser();
    }
  };

  if (showSelectStore) {
    return <InitialStoreSelector onGoBack={() => setShowSelectStore(false)} />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignContent: 'center',
        width: '360px',
      }}
    >
      <Typography variant="displaySM"
        fontWeight={600}
        sx={{ textAlign: 'center' }}>
        {t('title')}
      </Typography>
      <Typography color="textSecondary"
        variant="textMD"
        sx={{ textAlign: 'center', mt: '12px' }}>
        {t('welcomeMessage')}
      </Typography>
      <Box component="form"
        autoComplete="on">
        <Box sx={{ xs: 12, mt: 9 }}>
          <FormControl fullWidth>
            <TextField
              name="email"
              InputLabelProps={{ shrink: true }}
              value={email}
              onChange={handleEmailChange}
              onKeyDown={handleKeyPress}
              autoComplete="email"
              label={t('email')}
              error={!!emailError}
              helperText={emailError}
              FormHelperTextProps={{
                sx: {
                  marginLeft: 0, // Removes the default left margin
                },
              }}
              required
              fullWidth
            />
          </FormControl>
        </Box>

        <Box sx={{ xs: 12, pt: 8 }}>
          <FormControl fullWidth>
            <TextField
              name="password"
              InputLabelProps={{ shrink: true }}
              value={password}
              onChange={handlePasswordChange}
              onKeyDown={handleKeyPress}
              type={showPassword ? 'text' : 'password'}
              label={t('password')}
              error={!!passwordError}
              helperText={passwordError}
              FormHelperTextProps={{
                sx: {
                  marginLeft: 0, // Removes the default left margin
                },
              }}
              required
              autoComplete="current-password"
              InputProps={{
                sx: {
                  '& input::-ms-reveal': { display: 'none' },
                  '& input::-ms-clear': { display: 'none' },
                  '& input::-webkit-contacts-auto-fill-button': { display: 'none' },
                },
              }}
              fullWidth
            />
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox size='small'
                sx={{ py: '16px' }}
                checked={showPassword}
                onChange={(e) => setShowPassword(e.target.checked)}
              />
            }
            label="Show password"
          />
        </Box>
      </Box>
      <Grid sx={{ pt: 0 }}>
        <Button
          type="submit"
          variant="primary"
          fullWidth
          onClick={handleSubmit}
        >
          {t('signIn')}
        </Button>
      </Grid>
      <Grid container
        sx={{ display: 'flex', alignItems: 'center', paddingTop: 4 }}>
        <Grid item
          xs={12}
          sx={{ display: 'flex', justifyContent: 'start' }}>
          <Link href="#"
            onClick={props.switchDisplay}>
            {t('forgotPassword')}
          </Link>
        </Grid>
      </Grid>
      <Box sx={{ width: '100%', mt: 8 }}>
        <Divider>
          <Typography variant="textSM"
            color="textSecondary"
            sx={{ mx: 2 }}>
            OR
          </Typography>
        </Divider>
      </Box>
      <Grid sx={{ mt: 8 }}>
        <Button
          type="button"
          variant="secondary"
          startIcon={<img src={microsofticon} />}
          fullWidth
          onClick={() => window.location.href = AUTH0_LOGIN_URL}>
          {t('signInWithMicrosoft')}
        </Button>
      </Grid>
      <MessagesSnackbar open={openSnackbar}
        onClose={handleCloseSnackbar}
        message={snackbarMessage?.trim() ? snackbarMessage : t('invalidlogin')}
        severity='error'
        duration={null}
        horizontalPosition='left' />
    </Box>
  );
};

export default LoginForm;
